import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { announcementService } from "@/features/announcements/services/feedService";
import { PollResultsModal } from "@/features/announcements/components/PollResultsModal";
import type { AnnouncementPoll as Poll } from "@/features/announcements/types/feed";
import { Button } from "@/shared/components/ui/Button";
import { usePermissions } from "@/shared/hooks/usePermissions";

interface AnnouncementPollProps {
  rowId: number;
  initialPoll: Poll;
  isOfficer: boolean;
  onPollChange?: (poll: Poll) => void;
}

const errorText = (err: unknown, t: (key: string) => string, exists: (key: string) => boolean) => {
  if (!axios.isAxiosError(err)) return t("common.error");
  const body = err.response?.data as { error?: string; error_code?: string } | undefined;
  // The server already localises `error`; only prefer a local key when we have it.
  if (body?.error_code && exists(body.error_code)) return t(body.error_code);
  return body?.error || t("common.error");
};

/** Keep only notes for the options actually chosen, trimmed, non-empty. */
const notesFor = (optionIds: string[], notes: Record<string, string>) =>
  Object.fromEntries(
    optionIds.flatMap((id) => {
      const note = (notes[id] ?? "").trim();
      return note ? [[id, note]] : [];
    }),
  );

export const AnnouncementPoll = ({ rowId, initialPoll, isOfficer, onPollChange }: AnnouncementPollProps) => {
  const { t, i18n } = useTranslation();
  const { canWrite } = usePermissions();
  const [poll, setPoll] = useState(initialPoll);
  const [selected, setSelected] = useState<string[]>(initialPoll.my_option_ids);
  const [notes, setNotes] = useState<Record<string, string>>(initialPoll.my_notes ?? {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showResults, setShowResults] = useState(false);
  const coordinatorView = poll.options.some((option) => option.count !== undefined);
  const canManage = (isOfficer || coordinatorView) && canWrite("announcements");

  useEffect(() => {
    setPoll(initialPoll);
    setSelected(initialPoll.my_option_ids);
    setNotes(initialPoll.my_notes ?? {});
  }, [initialPoll]);

  useEffect(() => {
    if (!poll.is_open || !poll.closes_at) return;
    const timer = setInterval(() => {
      if (new Date(poll.closes_at!).getTime() > Date.now()) return;
      const next = { ...poll, is_open: false };
      setPoll(next);
      onPollChange?.(next);
      clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [poll, onPollChange]);

  const optionById = (id: string) => poll.options.find((option) => option.option_id === id);
  // Options in the current selection that demand an explanation.
  const noteOptions = selected
    .map(optionById)
    .filter((option): option is NonNullable<typeof option> => Boolean(option?.requires_note));
  const missingNote = noteOptions.some((option) => !(notes[option.option_id] ?? "").trim());
  // Single-select normally saves on click; a flagged option needs the note
  // first, so its submit is deferred to the button like multi-select.
  const sameSelection =
    selected.length === poll.my_option_ids.length && selected.every((id) => poll.my_option_ids.includes(id));
  const sameNotes = JSON.stringify(notesFor(selected, notes)) === JSON.stringify(notesFor(selected, poll.my_notes ?? {}));
  const needsSubmit = poll.allow_multiple || noteOptions.length > 0;
  const dirty = !sameSelection || !sameNotes;

  const refreshPoll = async (fallback: Poll) => {
    let next = fallback;
    try {
      const data: unknown = await announcementService.getAnnouncements();
      const current = Array.isArray(data) ? data.find((item: { row_id?: number }) => item.row_id === rowId) : undefined;
      next = (current?.poll as Poll | null | undefined) ?? fallback;
    } catch {
      // keep the fallback
    }
    setPoll(next);
    onPollChange?.(next);
    setSelected(next.my_option_ids);
    setNotes(next.my_notes ?? {});
  };

  const applyResponse = async (optionIds: string[]) => {
    const before = poll;
    const oldIds = new Set(poll.my_option_ids);
    const newIds = new Set(optionIds);
    const cleanNotes = notesFor(optionIds, notes);
    const respondentDelta = poll.has_responded === (optionIds.length > 0) ? 0 : optionIds.length > 0 ? 1 : -1;
    const next: Poll = {
      ...poll,
      my_option_ids: optionIds,
      my_notes: cleanNotes,
      has_responded: optionIds.length > 0,
      total_respondents: poll.total_respondents === undefined ? undefined : Math.max(0, poll.total_respondents + respondentDelta),
      options: poll.options.map((option) => ({
        ...option,
        count: option.count === undefined ? undefined : Math.max(0, option.count + (newIds.has(option.option_id) ? 1 : 0) - (oldIds.has(option.option_id) ? 1 : 0)),
      })),
    };
    setPoll(next);
    onPollChange?.(next);
    setSelected(optionIds);
    setNotes(cleanNotes);
    setBusy(true);
    setError("");
    try {
      if (optionIds.length) await announcementService.respondToPoll(rowId, optionIds, cleanNotes);
      else await announcementService.withdrawPollResponse(rowId);
    } catch (err) {
      await refreshPoll(before);
      setError(errorText(err, t, (key) => i18n.exists(key)));
    } finally {
      setBusy(false);
    }
  };

  const toggleLock = async () => {
    const before = poll;
    const locked = !poll.is_locked;
    setPoll({ ...poll, is_locked: locked, is_open: locked ? false : poll.is_open });
    setBusy(true);
    setError("");
    try {
      await announcementService.setPollLock(rowId, locked);
      // is_open is server-derived (lock + closes_at + bulletin window), so an
      // unlock cannot be reconstructed locally — read it back.
      await refreshPoll({ ...before, is_locked: locked, is_open: locked ? false : before.is_open });
    } catch (err) {
      await refreshPoll(before);
      setError(errorText(err, t, (key) => i18n.exists(key)));
    } finally {
      setBusy(false);
    }
  };

  const onPick = (optionId: string, checked: boolean) => {
    if (poll.allow_multiple) {
      setSelected((prev) => checked ? prev.filter((id) => id !== optionId) : [...prev, optionId]);
      return;
    }
    if (optionById(optionId)?.requires_note) {
      setSelected([optionId]);
      return;
    }
    void applyResponse([optionId]);
  };

  return <div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/40 p-4">
    <p className="text-xs text-gray-500">{t(poll.allow_multiple ? "feed.poll.pick_many" : "feed.poll.pick_one")}</p>
    <div className="mt-3 space-y-2">
      {[...poll.options].sort((a, b) => a.sort_order - b.sort_order).map((option) => {
        const checked = selected.includes(option.option_id);
        const denominator = poll.total_respondents || 0;
        const proportion = coordinatorView && denominator > 0 ? (option.count || 0) / denominator : 0;
        return <label key={option.option_id} className={`relative block overflow-hidden rounded-lg border bg-white p-3 ${poll.is_open ? "cursor-pointer border-gray-200" : "cursor-not-allowed border-gray-100 opacity-70"}`}>
          {coordinatorView && <span className="absolute inset-y-0 left-0 bg-indigo-100/70" style={{ width: `${Math.min(100, proportion * 100)}%` }} />}
          <span className="relative flex items-center gap-3 text-sm">
            <input type={poll.allow_multiple ? "checkbox" : "radio"} name={`poll-${rowId}`} checked={checked} disabled={!poll.is_open || busy} onChange={() => onPick(option.option_id, checked)} className="text-indigo-600 focus:ring-indigo-500" />
            <span className="flex-1">
              {option.label}
              {option.requires_note && <span className="ml-2 text-xs text-gray-500">({t("feed.poll.note_required_badge")})</span>}
            </span>
            {coordinatorView && <span className="font-medium text-gray-700">{option.count}</span>}
          </span>
        </label>;
      })}
    </div>
    {poll.is_open && noteOptions.map((option) => <div key={option.option_id} className="mt-3 space-y-1">
      <label className="block text-xs font-medium text-gray-700">
        {t("feed.poll.note_label", { option: option.label })}
      </label>
      <textarea
        value={notes[option.option_id] ?? ""}
        maxLength={500}
        rows={2}
        disabled={busy}
        placeholder={t("feed.poll.note_placeholder")}
        onChange={(e) => setNotes((prev) => ({ ...prev, [option.option_id]: e.target.value }))}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
      />
    </div>)}
    {!poll.is_open && <p className="mt-3 text-sm text-gray-500">{t(poll.is_locked ? "feed.poll.locked" : "feed.poll.closed")}</p>}
    {poll.is_open && <div className="mt-3 flex flex-wrap items-center gap-2">
      {needsSubmit && <Button size="sm" loading={busy} disabled={selected.length === 0 || missingNote || !dirty} onClick={() => applyResponse(selected)}>{t(poll.has_responded ? "feed.poll.update_answer" : "feed.poll.submit")}</Button>}
      {missingNote && <span className="text-xs text-amber-700">{t("feed.poll.note_required")}</span>}
      {poll.has_responded && <><span className="text-xs font-medium text-green-700">{t("feed.poll.you_answered")}</span>{!needsSubmit && <span className="text-xs text-gray-500">{t("feed.poll.change_answer")}</span>}<Button variant="ghost" size="sm" disabled={busy} onClick={() => applyResponse([])}>{t("feed.poll.withdraw")}</Button></>}
    </div>}
    {coordinatorView && <p className="mt-3 text-xs text-gray-500">{t("feed.poll.responses", { count: poll.total_respondents ?? 0 })}</p>}
    {canManage && <div className="mt-3 flex gap-2 border-t border-indigo-100 pt-3">
      <Button variant="secondary" size="sm" loading={busy} onClick={toggleLock}>{t(poll.is_locked ? "feed.poll.unlock" : "feed.poll.lock")}</Button>
      <Button variant="secondary" size="sm" onClick={() => setShowResults(true)}>{t("feed.poll.results")}</Button>
    </div>}
    {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    <PollResultsModal isOpen={showResults} rowId={rowId} canWrite={canManage} onClose={() => setShowResults(false)} onLockChange={(locked) => void refreshPoll({ ...poll, is_locked: locked, is_open: locked ? false : poll.is_open })} />
  </div>;
};
